import {
  PaymentIntentResult,
  SetupIntentResult,
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';
import ServiceError from '../../models/classes/ServiceError';
import { StripeServiceConfig } from '../../types/ServiceConfigurations';
import {
  CartUpdateFromIntent,
  IStripeService,
  IntentAddressContract,
  IntentUpdateContract,
  StripeInfoContract,
} from '../../types/Stripe';

const INTENT_ENDPOINT = '/api/v1/intents';

export default class StripeService implements IStripeService {
  private storeUrl: string;

  private stripeJs: Stripe | null;

  private stripeElements: StripeElements | null;

  constructor(config: StripeServiceConfig) {
    this.storeUrl = config?.apiUrl;
    this.stripeJs = null;
    this.stripeElements = null;
  }

  public setElements(stripeJs: Stripe, stripeElements: StripeElements): void {
    this.stripeJs = stripeJs;
    this.stripeElements = stripeElements;
  }

  // To Server API
  public async getIntent(intentId: string): Promise<CartUpdateFromIntent> {
    try {
      const intentResponse = await window.fetch(
        `${this.storeUrl}${INTENT_ENDPOINT}/${intentId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            vMethod: 'GET',
          },
        }
      );
      const result = (await intentResponse.json()) as CartUpdateFromIntent;
      return result;
    } catch (error) {
      throw StripeService.stripeServiceError(error as Error);
    }
  }

  public async postIntent(
    shippingAddress: IntentAddressContract | IntentUpdateContract,
    updateStripeInfo: (stripeInfo: StripeInfoContract) => void
  ): Promise<void> {
    try {
      const intentResponse = await window.fetch(
        `${this.storeUrl}${INTENT_ENDPOINT}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            vMethod: 'POST',
          },
          body: JSON.stringify(shippingAddress),
        }
      );

      const intentData = await intentResponse.json();

      updateStripeInfo({
        clientSecretKey: intentData.clientSecret,
        paymentIntentId: intentData.paymentIntentId,
        setupIntentId: intentData.setupIntentId,
      });
    } catch (error) {
      throw StripeService.stripeServiceError(error as Error);
    }
  }

  public async putIntent(
    shippingAddress: IntentAddressContract,
    stripeInfo: StripeInfoContract,
    updateStripeInfo: (stripeInfo: StripeInfoContract) => void
  ): Promise<void> {
    try {
      const paymentIntent = await window.fetch(
        `${this.storeUrl}${INTENT_ENDPOINT}/${stripeInfo.paymentIntentId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            vMethod: 'PUT',
          },
          body: JSON.stringify(shippingAddress),
        }
      );

      const intentData = await paymentIntent.json();

      updateStripeInfo({
        clientSecretKey: intentData.clientSecret,
        paymentIntentId: intentData.paymentIntentId,
        setupIntentId: intentData.setupIntentId,
      });
    } catch (error) {
      throw StripeService.stripeServiceError(error as Error);
    }
  }

  // To Stripe API
  public async updateIntent(
    useSetupIntent: boolean
  ): Promise<PaymentIntentResult | SetupIntentResult> {
    let stripeResponse;
    try {
      stripeResponse = useSetupIntent
        ? await this.confirmSetup()
        : await this.updatePaymentIntent();
    } catch (error) {
      throw StripeService.stripeServiceError(error as Error);
    }

    return stripeResponse;
  }

  // Used for Payment Intents
  private async updatePaymentIntent(): Promise<PaymentIntentResult> {
    this.initializationCheck();
    // .updatePaymentIntent is a beta method and not in Stripe.d.ts
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const result = await this.stripeJs.updatePaymentIntent({
      elements: this.stripeElements,
      params: {
        expand: ['payment_method'],
      },
    });

    return result;
  }

  // Used for Setup Intents
  private async confirmSetup(): Promise<SetupIntentResult> {
    this.initializationCheck();

    return (this.stripeJs as Stripe).confirmSetup({
      elements: this.stripeElements as StripeElements,
      confirmParams: {
        expand: ['payment_method'],
      },
      redirect: 'if_required',
    });
  }

  public async updateIntentForWallet(
    clientSecret: string,
    intentUpdate: IntentUpdateContract
  ): Promise<PaymentIntentResult | SetupIntentResult> {
    this.initializationCheck();
    // .updatePaymentIntent is a beta method and not in Stripe.d.ts
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.stripeJs.updatePaymentIntent({
      clientSecret,
      params: {
        expand: ['payment_method'],
        ...intentUpdate,
      },
    }) as PaymentIntentResult;
  }

  public handleNextAction(
    clientSecret: string | null
  ): Promise<PaymentIntentResult> {
    this.initializationCheck();
    if (!clientSecret) {
      throw StripeService.stripeServiceError(
        new Error('Unable to complete handle next action.')
      );
    }
    return (this.stripeJs as Stripe).handleNextAction({
      clientSecret,
    }) as Promise<PaymentIntentResult>;
  }

  private initializationCheck = (): void | never => {
    if (!this.stripeJs || !this.stripeElements) {
      throw StripeService.stripeServiceError(
        new Error('Stripe was not initialized')
      );
    }
  };

  private static stripeServiceError = (error: Error): ServiceError =>
    new ServiceError(
      'StripeService',
      error?.message.length > 0 ? error.message : 'An error occurred'
    );
}
